/**
 * ProviderShell — Phase 2 chrome for the `/dashboard/provider/*`
 * route tree.
 *
 * Constant-dark mobile-sheet chrome — bg hardcoded `#374751` to match
 * the desktop `<ProviderSidebar>` (see VR-note in that file). Avoids
 * the dark-mode `bg-pp-slate` flip bug that would collapse contrast
 * against the white-tinted inactive nav text.
 *
 * Phase 3 §1 — accepts an optional `children` prop. When provided
 * (via `<PublicPageRouter>` for an authed PooPartner viewing a
 * public route), renders that content in the main slot; otherwise
 * mounts the React Router `<Outlet>` for the provider sub-routes.
 *
 * Phase 3 §5 — at `<md:`, the sidebar collapses behind a hamburger
 * button in the topbar that opens a left-side `<Sheet>`.
 */
import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import ProviderSidebar, {
  ProviderSidebarBrand,
  ProviderSidebarNavBody,
} from "@/components/provider/ProviderSidebar";
import ProviderTopBar from "@/components/provider/ProviderTopBar";
import BusinessVerificationBanner from "@/components/partner/BusinessVerificationBanner";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";

export default function ProviderShell({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div
      className="min-h-screen flex bg-pp-v3-bg"
      style={{ paddingTop: "var(--admin-mode-bar-h, 0px)" }}
      data-testid="provider-shell"
    >
      <ProviderSidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center md:hidden border-b border-pp-border bg-pp-v3-bg px-2 py-2">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md text-pp-text"
                style={{ width: 44, height: 44 }}
                aria-label="Open PooPartner menu"
                data-testid="provider-shell-hamburger-btn"
              >
                <Menu size={24} aria-hidden="true" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-[280px] p-0 bg-[#374751] text-white border-none"
              data-testid="provider-shell-mobile-sheet"
            >
              <ProviderSidebarBrand />
              <nav className="px-3 pb-6" role="navigation" data-testid="provider-shell-mobile-nav">
                <ProviderSidebarNavBody
                  mobile
                  onItemClick={() => setDrawerOpen(false)}
                />
              </nav>
            </SheetContent>
          </Sheet>
        </div>
        <ProviderTopBar />
        <main
          className="flex-1 p-4 md:p-8 overflow-x-hidden"
          data-testid="provider-shell-main"
        >
          <BusinessVerificationBanner />
          {children || <Outlet />}
        </main>
      </div>
    </div>
  );
}
